import { useFormik } from "formik";
import ParticlesAuth from "pages/AuthenticationInner/ParticlesAuth";
import { useEffect, useState } from "react";
import { Button, Card, Col, Form, Image, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { createSelector } from "reselect";
import * as Yup from "yup";
import { loginUser, resetLoginFlag } from "../../slices/thunk";
import { fetchLatestPermissionsLogin } from "slices/auth/login/thunk";

import abgbPNGLOGO from "assets/images/abgbPNGLOGO.png";

const Signin = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [rememberMe, setRememberMe] = useState<boolean>(false);

  const selectAccountAndLogin = createSelector(
    (state: any) => state.Account,
    (state: any) => state.Login,
    (account, login) => ({
      user: account.user,
      error: login.error,
      loading: login.loading,
      errorMsg: login.errorMsg,
    })
  );

  const { user, error, errorMsg } = useSelector(selectAccountAndLogin);

  const [passwordShow, setPasswordShow] = useState(false);
  const [initialValues, setInitialValues] = useState({
    email: "",
    password: "",
  });

  // Load saved credentials on component mount
  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    const savedPassword = localStorage.getItem("rememberedPassword");
    const isRemembered = localStorage.getItem("rememberMe") === "true";

    if (isRemembered && savedEmail) {
      setInitialValues({
        email: savedEmail,
        password: savedPassword || "",
      });
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    if (user) {
      const updatedUserData =
        process.env.REACT_APP_DEFAULTAUTH === "firebase"
          ? user.multiFactor.user.email
          : user.email;
      const updatedUserPassword =
        process.env.REACT_APP_DEFAULTAUTH === "firebase" ? "" : user.password;

      setInitialValues({
        email: updatedUserData,
        password: updatedUserPassword,
      });
    }
  }, [user]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      email: Yup.string().required("Please Enter Your Email"),
      password: Yup.string().required("Please Enter Your Password"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        // Handle Remember Me
        if (rememberMe) {
          localStorage.setItem("rememberedEmail", values.email);
          // localStorage.setItem("rememberedPassword", values.password);
          localStorage.setItem("rememberMe", "true");
        } else {
          localStorage.removeItem("rememberedEmail");
          // localStorage.removeItem("rememberedPassword");
          localStorage.removeItem("rememberMe");
        }

        await dispatch(loginUser(values, navigate));

        // Wait a moment for state to update
        setTimeout(async () => {
          // Access user from Redux state
          const state = (window as any).store?.getState();
          const userId = state?.Account?.user?.user_id || state?.Login?.user?.user_id;

          console.log("User ID from state:", userId);

          if (userId) {
            const result = await dispatch(fetchLatestPermissionsLogin(userId));
            if (!result) {
              console.error("Failed to fetch latest permissions");
            }
          }
        }, 100);

        toast.success("User logged in successfully", { toastId: "loginSuccess" });
        navigate("/dashboard");
      } catch (err: any) {
        const errorMessage =
          typeof err === "string"
            ? err
            : err?.message || "Login failed. Please check credentials.";
        toast.error(errorMessage, { toastId: "loginError" });
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (errorMsg) {
      setTimeout(() => {
        dispatch(resetLoginFlag());
      }, 3000);
    }
  }, [dispatch, errorMsg]);

  document.title = "Login | Quotations Management";

  return (
    <ParticlesAuth>
      <Col xs={12} xxl={12} className="d-flex justify-content-center">
        <div style={{ maxWidth: 500, width: "100%" }}>
          <Card className="mb-0 border-0 shadow-none">
            <Card.Body className="p-sm-5 m-lg-4">
              <div className="text-center mb-0">
                <Image src={abgbPNGLOGO} alt="Logo" 
                  width={200}
                  // height={100}
                />
              </div>

              <div className="text-center mt-0">
                <h5 className="fs-3xl">Welcome Back</h5>
                <p className="text-muted">Sign in to continue to ABGB Quotations Management Dashboard</p>
              </div>

              <div className="p-2 mt-5">
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                  }}
                >
                  {/* Email Field */}
                  <Form.Group className="mb-3" controlId="formUsername">
                    <Form.Label>
                      Email <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      placeholder="Enter username"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.email || ""}
                      isInvalid={
                        validation.touched.email && !!validation.errors.email
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {validation.errors.email}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {/* Password Field */}
                  <Form.Group className="mb-3" controlId="formPassword">
                    <Form.Label>
                      Password <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="position-relative auth-pass-inputgroup mb-2">
                      <Form.Control
                        type={passwordShow ? "text" : "password"}
                        placeholder="Enter password"
                        name="password"
                        value={validation.values.password || ""}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        isInvalid={validation.touched.password && !!validation.errors.password}
                      />
                      <Form.Control.Feedback type="invalid">
                        {validation.errors.password}
                      </Form.Control.Feedback>
                      <Button
                        variant="link"
                        className="position-absolute end-0 top-0 text-decoration-none text-muted d-flex align-items-center justify-content-center"
                        type="button"
                        onClick={() => setPasswordShow((prev) => !prev)}
                        style={{ zIndex: 2, width: 35, fontSize: 15, marginRight: 5 }}
                      >
                        <i
                          className={
                            passwordShow ? "ri-eye-fill align-middle" : "ri-eye-off-fill align-middle"
                          }
                        ></i>
                      </Button>
                    </div>

                    {/* Remember Me and Forgot Password */}
                    <div className="d-flex justify-content-between align-items-center">
                      <Form.Check
                        type="checkbox"
                        label="Remember me"
                        id="rememberMe"
                        name="rememberMe"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      />
                      <Link
                        to={`${process.env.PUBLIC_URL}/forgot-password`}
                        className="text-muted"
                        style={{ fontSize: "0.875rem" }}
                      >
                        Forgot password?
                      </Link>
                    </div>
                  </Form.Group>

                  {/* Submit Button */}
                  <div className="mt-4">
                    <Button
                      className="btn btn-primary w-100"
                      type="submit"
                      disabled={loading}
                    >
                      {loading && <Spinner size="sm" />} Sign In
                    </Button>
                  </div>
                </Form>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Col>
    </ParticlesAuth>
  );
};

export default Signin;